import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useApiQuery } from "../../../api/hooks";
import type { DeliverableKind } from "../../../api/types";
import { Card, EmptyState, Loading, Pill, SheetIcon } from "../../../ui";
import { deliverablePath } from "../lib/paths";
import { readerStatusWord } from "../lib/words";
import { UploadForm } from "../upload/UploadForm";

export { partsToSend } from "../upload/UploadForm";

/** The screen at /deliverables/new: add a document for the agent to read. The form itself is in `upload/UploadForm`. */
export function UploadPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session");
  const navigate = useNavigate();
  const existing = useApiQuery("list_deliverables", {}, { staleTime: 10_000 });
  const list = existing.data ?? [];

  return (
    <div className="stack item-screen">
      <Card aria-labelledby="upload-title">
        <h1 id="upload-title">Add a document</h1>
        <p>Add a datasheet, a drawing or a list for the agent to read.</p>
      </Card>

      <UploadForm
        sessionId={sessionId}
        cancel={<Link to="/">Cancel</Link>}
        onUploaded={(made) => {
          const first = made[0];
          if (first) void navigate(deliverablePath(first.id));
        }}
      />

      <Card aria-labelledby="documents-here-title">
        <h2 id="documents-here-title">Documents in this discipline</h2>
        {existing.isPending ? (
          <Loading>Reading the documents</Loading>
        ) : list.length === 0 ? (
          <EmptyState title="No documents yet">Add the first one above. The agent can then read it and propose cards.</EmptyState>
        ) : (
          <ul className="document-list">
            {list.map((d) => (
              <li key={d.id} className="document">
                <SheetIcon kind={d.deliverable_kind as DeliverableKind | null | undefined} label={d.document_kind.name} />
                <span>
                  <Link to={deliverablePath(d.id)}>{d.name}</Link> <span className="muted">{d.document_kind.name}</span>
                </span>
                <Pill>{readerStatusWord(d.reader_status)}</Pill>
                <span className="muted">
                  {d.pages ? `${d.pages} ${d.pages === 1 ? "page" : "pages"}, ` : ""}
                  {d.spots_count} {d.spots_count === 1 ? "spot" : "spots"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
